/**
 * Action Item Detection Service
 *
 * Extracts action items from transcript segments using pattern matching.
 * Supports both explicit triggers (user intentionally noting something)
 * and implicit triggers (detecting commitments from natural speech).
 */

import type { ActionItem } from '@/types';

// ============================================================================
// Constants
// ============================================================================

/**
 * Explicit triggers - phrases users say intentionally to capture something
 * These result in high-confidence action items.
 */
export const EXPLICIT_ACTION_TRIGGERS: RegExp[] = [
  /let me note that/i,
  /note to self/i,
  /i'll capture that/i,
  /bookmark that/i,
  /let me write that down/i,
  /adding that to my list/i,
  /action item/i,
];

/**
 * Implicit triggers - natural language patterns that indicate commitments
 * These result in medium-confidence action items.
 */
export const IMPLICIT_ACTION_TRIGGERS: ImplicitTrigger[] = [
  // User commitments
  { pattern: /i'll send/i, type: 'send' },
  { pattern: /i'll share/i, type: 'send' },
  { pattern: /i'll follow up/i, type: 'follow_up' },
  { pattern: /let me get back to you/i, type: 'follow_up' },
  { pattern: /i'll schedule/i, type: 'schedule' },
  { pattern: /i'll connect you/i, type: 'introduce' },
  { pattern: /i'll introduce you/i, type: 'introduce' },
  { pattern: /i'll find out/i, type: 'research' },
  { pattern: /let me check on that/i, type: 'research' },
  { pattern: /i owe you/i, type: 'follow_up' },
  { pattern: /i'll make sure/i, type: 'follow_up' },
  { pattern: /i'll put together/i, type: 'create' },
  { pattern: /i'll draft/i, type: 'create' },

  // Prospect commitments (fromProspect: true means only match non-user speakers)
  { pattern: /we'll send/i, type: 'prospect_action', fromProspect: true },
  { pattern: /i'll talk to/i, type: 'prospect_action', fromProspect: true },
  { pattern: /let me check with/i, type: 'prospect_action', fromProspect: true },
  { pattern: /we'll get back/i, type: 'prospect_action', fromProspect: true },
  { pattern: /i'll discuss/i, type: 'prospect_action', fromProspect: true },
];

/** Maximum length for extracted action text */
const MAX_ACTION_TEXT_LENGTH = 100;

/** Minimum length for valid action text after trigger */
const MIN_ACTION_TEXT_LENGTH = 3;

// ============================================================================
// Types
// ============================================================================

interface ImplicitTrigger {
  pattern: RegExp;
  type: ActionItem['triggerType'];
  fromProspect?: boolean;
}

/**
 * Options for action item extraction
 */
export interface ExtractActionItemsOptions {
  /** Include action items detected from prospect speech */
  includeProspectActions?: boolean;
  /** Custom trigger phrase to add to explicit triggers */
  customTriggerPhrase?: string;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Clean extracted action text for display
 *
 * @param text - Raw text after the trigger phrase
 * @returns Cleaned text suitable for display
 *
 * @example
 * cleanActionText("you the proposal by Friday.")
 * // Returns: "the proposal by Friday"
 */
function cleanActionText(text: string): string {
  return text
    .replace(/^(you |them |that |this |the )/, '')  // Remove leading filler words
    .replace(/[.!?,;:]+$/, '')                       // Remove trailing punctuation
    .split(/[.!?]/)[0]                               // Take only first sentence
    .trim()
    .slice(0, MAX_ACTION_TEXT_LENGTH);
}

// ============================================================================
// Main Function
// ============================================================================

/**
 * Extract action items from a transcript segment.
 *
 * Scans the text for both explicit triggers (high confidence) and
 * implicit triggers (medium confidence). Respects speaker attribution
 * to differentiate user vs prospect commitments.
 *
 * @param text - The transcript text to analyze
 * @param timestamp - When in the session this was spoken (seconds from start)
 * @param speaker - Speaker ID (0 = user, 1+ = others/prospect)
 * @param options - Extraction options
 * @returns Array of detected action items (may be empty)
 *
 * @example
 * const items = extractActionItems(
 *   "I'll send you the proposal by Friday",
 *   120, // 2 minutes into session
 *   0,   // speaker 0 = user
 *   { includeProspectActions: true }
 * );
 * // Returns: [{
 * //   id: "...",
 * //   text: "the proposal by Friday",
 * //   triggerType: "send",
 * //   speaker: "user",
 * //   confidence: "medium",
 * //   ...
 * // }]
 */
export function extractActionItems(
  text: string,
  timestamp: number,
  speaker: number = 0,
  options: ExtractActionItemsOptions = {}
): ActionItem[] {
  const {
    includeProspectActions = true,
    customTriggerPhrase,
  } = options;

  const items: ActionItem[] = [];

  // Build trigger list with optional custom phrase
  const allExplicitTriggers = customTriggerPhrase
    ? [...EXPLICIT_ACTION_TRIGGERS, new RegExp(customTriggerPhrase, 'i')]
    : EXPLICIT_ACTION_TRIGGERS;

  // Check explicit triggers (high confidence)
  for (const trigger of allExplicitTriggers) {
    const match = text.match(trigger);
    if (match) {
      const afterTrigger = text.slice(match.index! + match[0].length).trim();

      if (afterTrigger.length > MIN_ACTION_TEXT_LENGTH) {
        // Clean common delimiters after trigger phrase
        const cleaned = afterTrigger.replace(/^[-—:]\s*/, '');
        items.push({
          id: crypto.randomUUID(),
          text: cleanActionText(cleaned) || 'See context',
          fullContext: text,
          timestamp,
          triggerType: 'explicit',
          speaker: 'user',
          confidence: 'high',
          completed: false,
        });
        // Only one explicit action per segment
        return items;
      }
    }
  }

  // Check implicit triggers (medium confidence)
  for (const trigger of IMPLICIT_ACTION_TRIGGERS) {
    // Skip prospect actions if not enabled
    if (trigger.fromProspect && !includeProspectActions) continue;

    // For prospect triggers, only match if speaker is not user
    if (trigger.fromProspect && speaker === 0) continue;

    // For user triggers, only match if speaker is user
    if (!trigger.fromProspect && speaker !== 0) continue;

    const match = text.match(trigger.pattern);
    if (match) {
      const afterTrigger = text.slice(match.index! + match[0].length).trim();
      const actionText = cleanActionText(afterTrigger);

      if (actionText.length > MIN_ACTION_TEXT_LENGTH - 1) {
        items.push({
          id: crypto.randomUUID(),
          text: actionText,
          fullContext: text,
          timestamp,
          triggerType: trigger.type,
          speaker: trigger.fromProspect ? 'prospect' : 'user',
          confidence: 'medium',
          completed: false,
        });
      }
    }
  }

  return items;
}

/**
 * Legacy function signature for backwards compatibility.
 * @deprecated Use extractActionItems with options object instead.
 */
export function extractActionItemsLegacy(
  text: string,
  timestamp: number,
  speaker: number = 0,
  includeProspectActions: boolean = true,
  customTriggerPhrase?: string
): ActionItem[] {
  return extractActionItems(text, timestamp, speaker, {
    includeProspectActions,
    customTriggerPhrase,
  });
}
